import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { GetFragmentDto } from '@app/modules/fragments/fragment.types';
import { useFragmentQuery } from '@app/modules/fragments/hooks/useFragment';
import {
  SOCIAL_EXPORT_FRAGMENT_SOURCE,
  useAvailableSocialExportSources,
} from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { useFragmentMediaUrlQuery } from '@app/modules/fragments/hooks/useFragmentMediaUrlQuery';
import { getProjectRenderedVideoUrl } from '@app/modules/fragments/screens/view/components/video-editions/get-project-rendered-video-url';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useSocialExportFactory } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { socialExport, webApi } from '@mi-front/api-clients';
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { ComponentType, useEffect, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

import { type BaseExportFormProps } from '@app/modules/social-export/hooks/useSocialExportFactory';

const publicationStartedMessage = defineMessage({
  id: 'fragments.socialExport.publicationStarted',
  defaultMessage: 'Publicación iniciada',
});

const startPublicationErrorMessage = defineMessage({
  id: 'fragments.socialExport.startPublicationError',
  defaultMessage: 'Ocurrió un error al iniciar la publicación, contacte a soporte',
});

const noAllowedSourceErrorMessage = defineMessage({
  id: 'fragments.socialExport.noAllowedSourceError',
  defaultMessage: 'No hay una fuente disponible para publicar en esta estación.',
});

export type SocialExportPublishForm = ComponentType<
  BaseExportFormProps<webApi.CreateSocialUploadDto, socialExport.SettingsGetConnection200>
>;

export interface UseSocialExportUploadPageResult {
  fragmentId: string;
  platform: SocialPlatform;
  fragmentQuery: ReturnType<typeof useFragmentQuery>;
  mediaUrlQuery: ReturnType<typeof useFragmentMediaUrlQuery>;
  settingsQuery: ReturnType<
    typeof socialExport.useSettingsGetConnection<socialExport.SettingsGetConnection200>
  >;
  projectsListQuery: UseQueryResult<
    webApi.ListProjectsVideoEditorProjectsQueryResponse,
    unknown
  > & {
    queryKey: ReturnType<typeof webApi.listProjectsVideoEditorProjectsQueryKey>;
  };
  selectedSource: string;
  setSelectedSource: (value: string) => void;
  projects: webApi.EnrichedVideoEditorProjectListItemDto[];
  isFragmentSourceAllowed: boolean;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedProject: boolean;
  renderedVideoUrl: string | null;
  PublishForm: SocialExportPublishForm;
  handleUpload: (fragment: GetFragmentDto, data: webApi.CreateSocialUploadDto) => Promise<void>;
}

/**
 * Owns routing, source selection, connection settings, rendered preview URL, and the
 * create-upload mutation for the social export upload flow.
 */
export function useSocialExportUploadPage(): UseSocialExportUploadPageResult {
  const { fragmentId, platform: platformParam } = useParams<{
    fragmentId: string;
    platform: string;
  }>();
  const platform = socialPlatforms.get(platformParam!.toUpperCase())!;
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const projectIdFromUrl = new URLSearchParams(search).get('projectId') ?? undefined;

  const [selectedSource, setSelectedSource] = useState<string>(
    () => projectIdFromUrl ?? SOCIAL_EXPORT_FRAGMENT_SOURCE,
  );

  const fragmentQuery = useFragmentQuery(fragmentId);
  const mediaUrlQuery = useFragmentMediaUrlQuery(fragmentId);
  const {
    projectsListQuery,
    projects,
    projectsWithRender,
    isFragmentSourceAllowed,
    hasAvailableSources,
  } = useAvailableSocialExportSources({
    fragmentId: fragmentId!,
    stationId: fragmentQuery.data?.stationId,
  });

  const selectedProject =
    selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE
      ? (projects.find((p) => p.id === selectedSource) ?? null)
      : null;

  const { url: renderedVideoUrl } = getProjectRenderedVideoUrl(selectedProject, {
    mode: 'playback',
  });

  const showMediaSelector = projectsWithRender.length > 0;
  const hasSelectedRenderedProject = projectsWithRender.some(
    (project) => project.id === selectedSource,
  );

  useEffect(() => {
    if (projectIdFromUrl) {
      setSelectedSource(projectIdFromUrl);
    }
  }, [projectIdFromUrl]);

  useEffect(() => {
    if (projectIdFromUrl || isFragmentSourceAllowed) return;
    if (selectedSource !== SOCIAL_EXPORT_FRAGMENT_SOURCE) return;

    const firstRenderedProjectId = projectsWithRender[0]?.id;
    if (!firstRenderedProjectId) return;

    setSelectedSource(firstRenderedProjectId);
  }, [isFragmentSourceAllowed, projectIdFromUrl, projectsWithRender, selectedSource]);

  const useExport = useSocialExportFactory(platform);

  const settingsQuery =
    socialExport.useSettingsGetConnection<socialExport.SettingsGetConnection200>(
      fragmentQuery.data?.stationId ?? '',
      platform.id,
      {
        query: {
          enabled: !!fragmentQuery.data?.stationId,
        },
      },
    );

  const uploadMutation = webApi.useCreateFragmentSocialUploads({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: webApi.getFragmentSocialUploadsByFragmentIdQueryKey(fragmentId!),
        });
        notifyInfo($t(publicationStartedMessage));
      },
      onError: (error) => {
        notifyError($t(startPublicationErrorMessage));
        console.log(error);
      },
    },
  });

  const handleUpload = async (fragment: GetFragmentDto, data: webApi.CreateSocialUploadDto) => {
    try {
      if (selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE && !isFragmentSourceAllowed) {
        notifyError($t(noAllowedSourceErrorMessage));
        return;
      }

      const sourceType =
        selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE
          ? webApi.CreateSocialUploadDtoSourceTypeEnum.FRAGMENT
          : webApi.CreateSocialUploadDtoSourceTypeEnum.VIDEO_EDITOR_PROJECT;
      const sourceId =
        selectedSource === SOCIAL_EXPORT_FRAGMENT_SOURCE ? fragment.id : selectedSource;

      await uploadMutation.mutateAsync({
        data: {
          sourceType,
          sourceId,
          platform: platform.id as webApi.CreateSocialUploadDtoPlatformEnumKey,
          data,
        },
      });
      pushTo(`/fragments/${fragment.id}/publish`);
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  const { PublishForm } = useExport();

  return {
    fragmentId: fragmentId!,
    platform,
    fragmentQuery,
    mediaUrlQuery,
    settingsQuery,
    projectsListQuery,
    selectedSource,
    setSelectedSource,
    projects,
    isFragmentSourceAllowed,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedRenderedProject,
    renderedVideoUrl,
    PublishForm: PublishForm as SocialExportPublishForm,
    handleUpload,
  };
}
